import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import { ThemeProvider } from "@steelyard/theme";
import { Toaster } from "react-hot-toast";
import "./index.css";

import { guardedDataStrategy, RootRoute, rootLoader, ROOT_ROUTE_ID } from "./pages/root";
import DashboardPage, { dashboardLoader } from "./pages/dashboard";
import SignInPage from "./pages/auth/sign-in";
import OtpPage from "./pages/auth/Otp";
import InvitationAcceptPage, { invitationLoader } from "./pages/auth/InvitationAccept";
import PasswordResetPage from "./pages/auth/PasswordReset";
import PasswordResetCompletePage from "./pages/auth/PasswordResetComplete";
import NotFound from "./pages/not-found";
import AppError from "./pages/app-error";
import AppLoader, { NavigationLoader } from "./components/app-loader";
import { useRecordVisits } from "./lib/navigation";
import PrintInvoicePage, { printInvoiceLoader } from "./pages/invoicing/Print";
import PrintCreditNotePage, { printCreditNoteLoader } from "./pages/invoicing/CreditNotePrint";

import { accountRoutes } from "./pages/account/routes";
import { catalogueRoutes } from "./pages/catalogue/routes";
import { websiteRoutes } from "./pages/website/routes";
import { customersRoutes } from "./pages/customers/routes";
import { ordersRoutes } from "./pages/orders/routes";
import { inventoryRoutes } from "./pages/inventory/routes";
import { importsRoutes } from "./pages/imports/routes";
import { reportsRoutes } from "./pages/reports/routes";
import { invoicingRoutes } from "./pages/invoicing/routes";
import { invoicingRegistrationRoutes } from "./pages/invoicing/registration-routes";
import { financeRoutes } from "./pages/finance/routes";
import { dispatchRoutes } from "./pages/dispatch/routes";
import { settingsRoutes } from "./pages/settings/routes";
import { integrationsRoutes } from "./pages/integrations/routes";
import { packingRoutes } from "./pages/packing/routes";
import { usersRoutes } from "./pages/users/routes";
import { invitationsRoutes } from "./pages/invitations/routes";
import { auditRoutes } from "./pages/audit/routes";
import { notificationsRoutes } from "./pages/notifications/routes";
import { roleMatrixRoutes } from "./pages/role-matrix/routes";
import { appVersionsRoutes } from "./pages/app-versions/routes";
import { globalMessagesRoutes } from "./pages/global-messages/routes";
import { contentRoutes } from "./pages/content/routes";

/**
 * The full route tree — every feature area from the functionality inventory, ported from
 * apps/admin/app/(app)/* and (auth)/*. Each of the ten `*Routes` arrays was built by a separate
 * parallel migration pass and merged here; see each routes/*.tsx file for that area's specific
 * business-logic notes.
 */
/**
 * Pathless wrapper for every route. GlobalDrawer/GlobalModal live in RootRoute (pages/root.tsx), inside the menu/session providers their views read.
 * `NavigationLoader` is the blurred logo overlay while another page's loaders run.
 */
function RootLayout() {
  useRecordVisits();
  return (
    <>
      <Outlet />
      <NavigationLoader />
    </>
  );
}

const router = createBrowserRouter([
  {
    Component: RootLayout,
    // Shown while the first match's loaders run (rootLoader's /me read, a print route's invoice).
    HydrateFallback: AppLoader,
    children: [
      { path: "/sign-in", Component: SignInPage },
      { path: "/otp", Component: OtpPage },
      { path: "/invitations/:token", Component: InvitationAcceptPage, loader: invitationLoader },
      { path: "/password-reset", Component: PasswordResetPage },
      { path: "/password-reset/:token", Component: PasswordResetCompletePage },
      // Deliberately outside the app shell (no sidebar/header) — see Print.tsx's own note on why a
      // legal document's route must never nest inside a navigation landmark.
      { path: "/invoicing/:invoiceNumber/print", Component: PrintInvoicePage, loader: printInvoiceLoader },
      { path: "/invoicing/credit-notes/:creditNoteNumber/print", Component: PrintCreditNotePage, loader: printCreditNoteLoader },
      {
        id: ROOT_ROUTE_ID,
        path: "/",
        Component: RootRoute,
        loader: rootLoader,
        errorElement: <AppError />,
        children: [
          { index: true, Component: DashboardPage, loader: dashboardLoader },
          ...accountRoutes,
          ...catalogueRoutes,
          ...websiteRoutes,
          ...customersRoutes,
          ...ordersRoutes,
          ...inventoryRoutes,
          ...importsRoutes,
          ...reportsRoutes,
          ...invoicingRoutes,
          ...invoicingRegistrationRoutes,
          ...financeRoutes,
          ...dispatchRoutes,
          ...settingsRoutes,
          ...integrationsRoutes,
          ...packingRoutes,
          ...usersRoutes,
          ...invitationsRoutes,
          ...auditRoutes,
          ...notificationsRoutes,
          ...roleMatrixRoutes,
          ...appVersionsRoutes,
          ...globalMessagesRoutes,
          ...contentRoutes,
          { path: "*", Component: NotFound },
        ],
      },
    ],
  },
], { dataStrategy: guardedDataStrategy });

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider defaultTheme="light">
      <Toaster position="top-right" />
      <RouterProvider router={router} />
    </ThemeProvider>
  </StrictMode>,
);
